// src/features/projects/ProjectsPage.jsx
//
// PROJ-PR-1/PR-2 — Executive Portfolio view for Projects.
// Route: VIEW_PROJECTS = "projects" in App.jsx.
//
// Layout (top to bottom):
//   1. KpiTileRow — six tiles (Active, At Risk, Exits, Net seats, Delivering, Budget)
//   2. Portfolio toolbar (Search + ConsolidatedFilter + Clear | density + Add)
//   3. Projects table with one inline-expanded project at a time
//
// Architecture:
//   - Fetches from GET /api/projects via projectsClient.getProjects().
//   - Child objects fetched lazily: first time each module tab is opened.
//   - All filtering is client-side (applyProjectCfFilters — multi-select aware).
//   - Add Project form is a modal, role-gated to editor+ at both UI and API.
//   - Colors route ONLY through T tokens or var(--v2-*) CSS vars. (Principle B5)
//   - Decision and category-budget APIs remain available for future slices but
//     are intentionally not loaded by this lean portfolio surface.
//
// PROJECTS-LANDING-ALIGN-TO-OFFICES (S1):
//   Structural alignment to OfficesPage layout — KpiTileRow, ConsolidatedFilter,
//   ViewControls, SearchInput, density toggle. No data or business logic changes.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { projectTabFromPath, buildProjectsTabUrl, buildProjectUrl, buildProjectUpdatesUrl, buildWeeklyStateUrl, projectRefFromPath, projectUpdatesIdFromPath, resolveProjectRef, projectCanonicalRef, PROJECT_DRAWER_TABS, validProjectWeek, validWeeklyView } from "../../services/buildUrl.js";
import { readRecordReturn } from "../../services/recordReturn.js";
import { T, PROJECT_TYPE, PROJECT_PHASE, REGIONS } from "../../app/tokens.js";
import { APP_NAME } from "../../constants/branding.js";
import { mapGanttPhase } from "./hub/ganttModel.js";
import { S } from "../../app/styles.js";
import { ProjectsScrollLayout } from "./ProjectsScrollLayout.jsx";
import { ProjectsFxStatus } from "./ProjectsFxStatus.jsx";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiPopover, HOME_KPI_TOOLTIP_STYLE } from "../../components/KpiPopover.jsx";
import { KPI_TILE_ROW_HEIGHT } from "../plan/KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { CHECKED_FILTER_VERSION, normalizeCheckedSelections, checkedPredicateSelections, allOptionValues, compactCheckedSelections, withFacetCounts } from "../../utils/checkedSelections.js";
import { PROJECT_STAGES, DEFAULT_PROJECT_STAGES, isCurrentProject } from "../../lib/projectStage.js";
import { LIST_PRESENTATION } from "../../v2/components/listPresentation.js";
import { InlineFilterToolbar, InlineFilterChips, InlineFilterClear } from "../../v2/components/InlineFilterToolbar.jsx";
import { activeFilterGroups, restrictedFilterGroups, deriveFilterHiddenSearch, relaxFilterGroups } from "../../utils/filterHiddenSearch.js";
import { FilterHiddenSearchHint } from "../../v2/components/FilterHiddenSearchHint.jsx";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import {
  ViewControls, CompactSwitch, DENSITY_KIND,
} from "../../v2/components/ViewControls.jsx";
import { AlertChip } from "../../v2/components/AlertChip.jsx";
import { SortControl } from "../../v2/components/SortControl.jsx";
import { ToolbarAddButton } from "../../v2/components/ToolbarAddButton.jsx";
import {
  getAllNextMilestones,
  getProjectSourceInventory,
} from "../../data/projectsClient.js";
import {
  computeProjectStats,
  convertProjectBudgetToUsd,
  applyProjectScope,
  mapProjectPhaseToDisplayPhase,
  PROJECT_DISPLAY_PHASES,
  isCapitalOrMoveProject,
  PROJECT_SCOPE,
} from "./projectFilters.js";
import { fmtInt } from "../../utils/format.js";
import { log } from "../../utils/logger.js";
import { useFxRates } from "../../data/useFxRates.js";
import {
  VALID_PROJECT_SORT_KEYS,
  UI_PREFS_PROJECTS_V1,
} from "../../services/portfolioStatePersistence.js";
import { useUiPrefs } from "../../v2/hooks/useUiPrefs.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { ProjectEditModal } from "./ProjectEditPanel.jsx";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { useCurrentProjectWeek } from "./weekly/ProjectWorkingUpdates.jsx";
import { useLandingWeekly } from "./briefing/useLandingWeekly.js";
import { projectAlertSnapshot, readAlertProjects } from "../../services/projectAlertProjects.js";
import {
  buildProjectAlertMessages,
} from "./briefing/landingModel.js";
import { ProjectsPortfolioList } from "./ProjectsPortfolioList.jsx";
import { ProjectRecordDrawer } from "./ProjectRecordDrawer.jsx";
import { weeklyParticipation } from "../../lib/weeklyParticipation.js";
import { enrichPortfolioProject, comparePortfolioTargets } from "./portfolioRowModel.js";
import { ProjectsHubBar } from "./hub/ProjectsHubBar.jsx";
import { ProjectsNavigation } from "./ProjectsChrome.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { ProjectRiskRegister } from "./risk/ProjectRiskRegister.jsx";
import { ProjectsSignals } from "./hub/ProjectsSignals.jsx";
import { ProjectsContracts } from "./hub/ProjectsContracts.jsx";
import { normalizeProjectsTabs } from "../../app/projectsTabs.js";
import { WeeklyOverview } from "./weekly/WeeklyOverview.jsx";
import { UpdatesKpiBand } from "./weekly/UpdatesKpiBand.jsx";
import { MyWeeklyUpdate } from "./weekly/MyWeeklyUpdate.jsx";
import { PeopleRolesMatrix } from "./hub/PeopleRolesMatrix.jsx";
import { Modal } from "../../components/Modal.jsx";
import { ProjectsGantt } from "./hub/ProjectsGantt.jsx";
import { ProjectsTravel } from "./travel/ProjectsTravel.jsx";
import { ProjectsTimelineKey, ProjectsTimelineRange } from "./hub/ProjectsTimelineControls.jsx";
import { ProjectsFinancials } from "./hub/ProjectsFinancials.jsx";
import { ProjectFinancialsImportModal } from "./hub/ProjectFinancialsImportModal.jsx";
import {
  HUB_LENS, canViewHubFinancials, canExportHubFinancials, isActiveHubProject,
} from "./hub/hubAccess.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const DENSITY_COMFORTABLE = "comfortable";
const DENSITY_COMPACT      = "compact";

const VIEW_LIST  = "list";

const WRITE_ROLES = new Set(["admin", "editor"]);
// Future opt-in only. Never bypass actual admin or financial sensitivity gates.
const SHOW_LENS_FINANCIAL_IMPORT = false;

// ─── Filter dimensions config (for ConsolidatedFilter) ───────────────────────

// Major projects is an explicit Scope restriction inside Filter; it uses the
// existing isCapitalOrMoveProject predicate via applyProjectScope.
const SCOPE_DIMENSION = {
  id: "scope",
  label: "Scope",
  restriction: true,
  options: [{ value: PROJECT_SCOPE.CAPITAL_MOVES, label: "Major projects" }],
};

const FILTER_DIMENSIONS_BASE = [
  {
    id: "stage", label: "Stage", defaultValues: DEFAULT_PROJECT_STAGES,
    options: Object.entries(PROJECT_STAGES).map(([value, label]) => ({ value, label })),
  },
  {
    id: "type",
    label: "Type",
    options: Object.entries(PROJECT_TYPE).map(([k, v]) => ({ value: k, label: v.label })),
  },
  {
    id: "region",
    label: "Region",
    options: REGIONS.map((r) => ({ value: r, label: r })),
  },
  {
    id: "phase",
    label: "Phase",
    options: Object.entries(PROJECT_PHASE).map(([k, v]) => ({ value: k, label: mapGanttPhase(k)?.label || v.label })),
  },
  {
    id: "health",
    label: "Health",
    options: [
      { value: "on_track", label: "On Track"  },
      { value: "at_risk",  label: "At Risk"   },
      { value: "critical", label: "Critical"  },
    ],
  },
];

// ─── Client-side multi-select filter ─────────────────────────────────────────
// OR within a dimension, AND across dimensions. Does NOT touch projectFilters.js.

export function buildProjectFilterDimensions(projects) {
  const owners = [...new Set(projects.map(p => p.internalOwner).filter(Boolean))].sort();
  return [...FILTER_DIMENSIONS_BASE, {
    id: "owner", label: "Owner", options: owners.map(value => ({ value, label: value })),
  }];
}

export function projectCheckedSelections(prefs, dimensions) {
  const raw = prefs.cfSelections ?? {};
  return normalizeCheckedSelections(dimensions, {
    ...raw,
    ...(raw.stage === "all" ? { stage: allOptionValues(dimensions[0]) } : {}),
  }, { stage: DEFAULT_PROJECT_STAGES }, {
    legacy: prefs.filterVersion !== CHECKED_FILTER_VERSION, emptyMeansNone: ["stage"],
  });
}

export function hasCompletePhaseFilter(selections) {
  return selections.phase?.includes("complete")
    && FILTER_DIMENSIONS_BASE.find(dim => dim.id === "phase").options.some(option => !selections.phase.includes(option.value));
}

export function applyProjectCfFilters(projects, cfSelections, search, checkedDimensions = null, countGroup = null) {
  if (!Array.isArray(projects)) return [];
  if (checkedDimensions) {
    cfSelections = checkedPredicateSelections(checkedDimensions, cfSelections, false, countGroup);
    if (!cfSelections) return [];
    cfSelections = { stage: "all", ...cfSelections };
  }
  let result = projects;
  if (cfSelections.stage !== "all") {
    const stages = cfSelections.stage ?? DEFAULT_PROJECT_STAGES;
    result = result.filter(p => stages.includes(p.stage));
  }

  if (search) {
    // SEARCH-SCOPE-STANDARDIZE — Matches: name · locationName (city) ·
    // locationAlias (office alias) · locationId · locationRegion.
    // oneLineSummary removed (description text pollutes identity matches).
    // Uses the shared matcher for diacritic-insensitive matching.
    const q = search;
    result = result.filter((p) =>
      matchesFoldedQuery(q, p.name) ||
      matchesFoldedQuery(q, p.locationName) ||
      matchesFoldedQuery(q, p.locationAlias) ||
      matchesFoldedQuery(q, p.locationId) ||
      matchesFoldedQuery(q, p.locationRegion)
    );
  }

  if (cfSelections.type?.length > 0) {
    result = result.filter((p) => cfSelections.type.includes(p.projectType));
  }
  if (cfSelections.region?.length > 0) {
    result = result.filter((p) =>
      cfSelections.region.some((r) =>
        typeof p.locationId === "string" && p.locationId.startsWith(r + "-")
      )
    );
  }
  if (cfSelections.phase?.length > 0) {
    result = result.filter((p) => cfSelections.phase.includes(p.phase));
  }
  if (cfSelections.health?.length > 0) {
    result = result.filter((p) => cfSelections.health.includes(p.statusOverall));
  }
  if (cfSelections.owner?.length > 0) {
    result = result.filter((p) => cfSelections.owner.includes(p.internalOwner));
  }

  return result;
}

// ─── Client-side sort ─────────────────────────────────────────────────────────

export function applyProjectSort(projects, sortKey, sortDir, rates = null) {
  if (!sortKey) return projects;
  const comparePrimary = (a, b) => {
    if (sortKey === "phase") {
      const rank = (project) => {
        const d = mapProjectPhaseToDisplayPhase(project.phase);
        if (d === "complete") return PROJECT_DISPLAY_PHASES.length;
        const i = PROJECT_DISPLAY_PHASES.findIndex(([key]) => key === d);
        return i < 0 ? null : i;
      };
      const ra = rank(a); const rb = rank(b);
      if ((ra === null) !== (rb === null)) return ra === null ? 1 : -1;
      if (ra === null) return 0;
      return (ra - rb) * sortDir;
    }
    if (sortKey === "fdobDate" || sortKey === "targetCompletionDate") {
      return comparePortfolioTargets(a, b, sortDir);
    }
    const valueFor = (project) => sortKey === "approvedBudget"
      ? (project.portfolio && "budgetUsd" in project.portfolio
        ? project.portfolio.budgetUsd
        : convertProjectBudgetToUsd(project, rates))
      : project[sortKey];
    const rawA = valueFor(a);
    const rawB = valueFor(b);
    if (sortKey === "approvedBudget") {
      const aMissing = rawA == null || !Number.isFinite(Number(rawA));
      const bMissing = rawB == null || !Number.isFinite(Number(rawB));
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      if (aMissing && bMissing) return 0;
      return (Number(rawA) - Number(rawB)) * sortDir;
    }
    const av = (rawA ?? "").toString().toLowerCase();
    const bv = (rawB ?? "").toString().toLowerCase();
    if (av < bv) return -sortDir;
    if (av > bv) return sortDir;
    return 0;
  };
  return [...projects].sort((a, b) => comparePrimary(a, b)
    || String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
    || String(a.id).localeCompare(String(b.id)));
}

// Canonical ownership/tracks for the Portfolio drawer come from the existing
// shared current-week overview read; failure is unavailable with retry.
export function portfolioParticipation(weekState, projectId, onRetry) {
  if (!projectId) return null;
  if (weekState?.error) return { status: "error", error: weekState.error, onRetry };
  if (!weekState?.data) return { status: "loading" };
  const row = (weekState.data.projects || []).find((p) => p.id === projectId);
  if (!row) return { status: "unavailable", reason: "This project is not in the current week's Updates, so ownership and tracks are unavailable." };
  return { status: "ok", part: weeklyParticipation(row) };
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {Function} [props.onRowClick]           Optional compatibility callback for row selection
 * @param {string}   props.currentRole           "admin" | "editor" | "viewer"
 * @param {number}   [props.refreshKey]          Increment to force a refetch
 * @param {object}   [props.projectPatch]        When set to an updated project object, in-place replaces
 *                                               that project in the list without a full refetch.
 * @param {string}   [props.locationIdFilter]   Exact office/location ID from the canonical deep link.
 * @param {Function} [props.onClearLocationFilter] Removes a URL-scoped office filter.
 */
export function ProjectsPage({
  onRowClick,
  onExpandedProjectChange,
  onProjectUpdated,
  initialExpandedProjectId = null,
  initialWeeklyArchive = null,
  currentRole,
  authIdentity = "unbound",
  refreshKey = 0,
  projectPatch = null,
  locationIdFilter = null,
  onClearLocationFilter,
}) {
  const [projects,    setProjects]    = useState([]);
  const [alertProjectState, setAlertProjectState] = useState(() => ({
    authIdentity, refreshKey, data: projectAlertSnapshot(authIdentity),
  }));
  const alertProjects = alertProjectState.authIdentity === authIdentity && alertProjectState.refreshKey === refreshKey
    ? alertProjectState.data : null;
  // Current record metadata is separate from selected-week evidence and filters.
  const connectionsByProjectId = useMemo(
    () => new Map(projects.map(project => [project.id, project.connections])),
    [projects],
  );
  const [loading,     setLoading]     = useState(true);
  const [fetchErr,    setFetchErr]    = useState(null);
  const [projAnimKey, setProjAnimKey] = useState(0);
  const sensitivity = useSensitivityPolicy();
  const { flagsReady, projectsTabs } = useFeatureFlagContext();
  const governedTabs = normalizeProjectsTabs(projectsTabs);
  const isTabEnabled = (lens) => governedTabs.some((row) => row.lens === lens && row.enabled);
  const showRiskRegister = isTabEnabled(HUB_LENS.RISK_REGISTER);
  const canSeeBudget = sensitivity.canSee("projects.budget");
  const { rates, loading: fxLoading, error: fxError } = useFxRates();
  const fxRatesKey = rates ? JSON.stringify(rates) : "";
  const fxRatesRef = useRef(rates);
  fxRatesRef.current = rates;
  // Mirrors OfficesPage's kpiReadyCountRef pattern: projects=+1, milestones=+1.
  // Animation fires only once both are ready so the milestone-backed list and
  // KPI strip settle together on first load.
  const kpiReadyCountRef = useRef(0);

  // ── Persisted UI preferences (survive tab close / browser restart) ──────────
  // clearPrefs unused here — Projects has no Clear-only reset that skips view/density.
  const [projPrefs, setProjPrefs] = useUiPrefs(UI_PREFS_PROJECTS_V1, {
    viewMode:      VIEW_LIST,
    density:       DENSITY_COMFORTABLE,
    cfSelections:  {},
    search:        "",
    projectSortKey: null,
    projectSortDir: 1,
    scope:          PROJECT_SCOPE.ALL,
  });

  const viewMode       = VIEW_LIST; // Board toggle hidden; always list (projPrefs.viewMode ignored)
  const density        = projPrefs.density        ?? DENSITY_COMFORTABLE;
  const filterDimensions = useMemo(() => buildProjectFilterDimensions(projects), [projects]);
  const cfSelections = useMemo(() => projectCheckedSelections(projPrefs, filterDimensions),
    [projPrefs.cfSelections, projPrefs.filterVersion, filterDimensions]);
  const search         = projPrefs.search         ?? "";
  // Null is the legacy "no explicit choice" preference; keep it in storage,
  // but show the same date default for all roles (including budget-restricted).
  const savedSortKey = projPrefs.projectSortKey;
  const projectSortKey = savedSortKey === "approvedBudget" && !canSeeBudget
    ? "fdobDate"
    : (savedSortKey === "targetCompletionDate" ? "fdobDate" : (savedSortKey ?? "fdobDate"));
  const projectSortDir = savedSortKey == null || (savedSortKey === "approvedBudget" && !canSeeBudget)
    ? 1
    : (projPrefs.projectSortDir ?? 1);
  // All is the clean/default scope. A stored explicit Major restriction stays
  // truthful: it is applied and surfaced as an active Scope filter.
  const projectScope   = projPrefs.scope === PROJECT_SCOPE.CAPITAL_MOVES ? PROJECT_SCOPE.CAPITAL_MOVES : PROJECT_SCOPE.ALL;
  const scopedLocationId = typeof locationIdFilter === "string" && locationIdFilter.length > 0
    ? locationIdFilter
    : null;

  const setDensity       = (v) => setProjPrefs((s) => ({ ...s, density:        typeof v === "function" ? v(s.density)        : v }));
  const setCfSelections = (v) => setProjPrefs(s => ({
    ...s, filterVersion: CHECKED_FILTER_VERSION,
    cfSelections: compactCheckedSelections(filterDimensions,
      typeof v === "function" ? v(projectCheckedSelections(s, filterDimensions)) : v,
      { stage: DEFAULT_PROJECT_STAGES },
      Object.keys(s.cfSelections ?? {}).filter(id => s.cfSelections[id]?.length === 0 && s.filterVersion === CHECKED_FILTER_VERSION)),
  }));
  const setSearch        = (v) => setProjPrefs((s) => ({ ...s, search:         typeof v === "function" ? v(s.search)         : v }));
  const setProjectSortKey= (v) => setProjPrefs((s) => ({ ...s, projectSortKey: typeof v === "function" ? v(s.projectSortKey) : v }));
  const setProjectSortDir= (v) => setProjPrefs((s) => ({ ...s, projectSortDir: typeof v === "function" ? v(s.projectSortDir) : v }));
  const setProjectScope  = (v) => setProjPrefs((s) => ({ ...s, scope: v }));

  // Modal
  const [modalOpen,   setModalOpen]   = useState(false);
  const [timelineRange, setTimelineRange] = useState("24");

  // One expanded row is the selection state. Keeping this in the list page
  // makes the single-open-row rule explicit and also supports deep links.
  // Full inline detail is routed (deep link / Open full project). The briefing
  // peek is local list state and never touches the routed project ID.
  const [expandedProjectId, setExpandedProjectId] = useState(initialExpandedProjectId);
  const [peekProjectId, setPeekProjectId] = useState(null);
  const selectedProjectId = peekProjectId;

  const [sourceInventory, setSourceInventory] = useState({ portfolioSources: [], projectSources: [] });
  const canWrite = WRITE_ROLES.has(currentRole);

  const routerLocation = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(routerLocation.search);
  const legacyWeek = validProjectWeek(query.get("weeklyWeek") || initialWeeklyArchive?.week);
  const overviewWeek = validProjectWeek(query.get("week")) || legacyWeek;
  const overviewView = validWeeklyView(query.get("view"));
  // Task #9085: /projects/updates/{id} is the canonical Updates reader;
  // /projects/{code|id} is the record drawer over the Portfolio list.
  const readerProjectId = projectUpdatesIdFromPath(routerLocation.pathname);
  const drawerRef = readerProjectId ? null : projectRefFromPath(routerLocation.pathname);
  const overviewProject = readerProjectId || query.get("weeklyProject") || initialWeeklyArchive?.projectId || null;
  const hubLens = projectTabFromPath(routerLocation.pathname) || (drawerRef && routerLocation.state?.financialsReturn ? HUB_LENS.FINANCIALS : drawerRef && routerLocation.state?.travelReturn ? HUB_LENS.TRAVEL : drawerRef && routerLocation.state?.participantsReturn ? HUB_LENS.PEOPLE : readerProjectId || legacyWeek ? HUB_LENS.WEEKLY : HUB_LENS.PORTFOLIO);
  const setHubLens = useCallback((lens) => {
    const allowed = governedTabs.some((row) => row.lens === lens && row.enabled);
    const target = `${buildProjectsTabUrl(allowed ? lens : HUB_LENS.PORTFOLIO, routerLocation.search)}${routerLocation.hash}`;
    if (target !== `${routerLocation.pathname}${routerLocation.search}${routerLocation.hash}`) navigate(target);
  }, [navigate, routerLocation, projectsTabs]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeWeeklyState = useCallback((patch) => {
    const target = buildWeeklyStateUrl(routerLocation.search, patch, readerProjectId);
    if (target !== `${routerLocation.pathname}${routerLocation.search}`) navigate(target);
  }, [navigate, routerLocation, readerProjectId]);
  // Legacy reader bookmarks (/projects/weekly?weeklyProject=X) → canonical
  // reader path. One replace; the target has no weeklyProject so no loop.
  useEffect(() => {
    if (readerProjectId || !query.get("weeklyProject")) return;
    if (projectTabFromPath(routerLocation.pathname) !== HUB_LENS.WEEKLY && !legacyWeek) return;
    navigate(buildWeeklyStateUrl(routerLocation.search, {}), { replace: true });
  }, [readerProjectId, routerLocation.pathname, routerLocation.search]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!legacyWeek || !query.has("weeklyWeek") || !isTabEnabled(HUB_LENS.WEEKLY)) return;
    navigate(buildWeeklyStateUrl(routerLocation.search, {
      week: overviewWeek, weeklyWeek: null,
    }, readerProjectId), { replace: true });
  }, [legacyWeek, overviewWeek, routerLocation.search, navigate]); // eslint-disable-line react-hooks/exhaustive-deps
  const [financialImportOpen, setFinancialImportOpen] = useState(false);
  const [financialRefresh, setFinancialRefresh] = useState(0);
  const canSeeHubFinancials = canViewHubFinancials(sensitivity);
  const canExportFinancials = canExportHubFinancials(sensitivity);
  const activeLens = (hubLens === HUB_LENS.FINANCIALS && !canSeeHubFinancials)
    || !isTabEnabled(hubLens) || !flagsReady ? HUB_LENS.PORTFOLIO : hubLens;
  useEffect(() => {
    if (activeLens !== HUB_LENS.PEOPLE) return;
    const previousTitle = document.title;
    document.title = `Team · Projects · ${APP_NAME}`;
    return () => { document.title = previousTitle; };
  }, [activeLens]);
  // Risk scope belongs only to this lens. Portfolio preferences and KPI inputs
  // remain untouched, including when navigating away from a scoped register.
  const riskProjectId = activeLens === HUB_LENS.RISK_REGISTER ? query.get("riskProject") : null;
  const riskProjects = useMemo(() => riskProjectId
    ? projects.filter((p) => p.id === riskProjectId && isActiveHubProject(p))
    : null, [projects, riskProjectId]);
  const hiddenHubLens = !isTabEnabled(hubLens);
  useEffect(() => {
    if (flagsReady && hiddenHubLens) {
      // Retain legacy week context without letting weeklyWeek select Updates
      // again once the hidden Signals URL reaches the bare Projects landing.
      const patch = hubLens === HUB_LENS.SIGNALS
        ? { weeklyWeek: null, ...(legacyWeek && !validProjectWeek(query.get("week")) ? { week: legacyWeek } : {}) }
        : {};
      const portfolioUrl = buildProjectsTabUrl(HUB_LENS.PORTFOLIO, routerLocation.search, patch);
      // Hidden Signals bookmarks return to the collection, not a new lens URL.
      const target = hubLens === HUB_LENS.SIGNALS
        ? portfolioUrl.replace(/^\/projects\/portfolio(?=\?|$)/, "/projects") + routerLocation.hash
        : portfolioUrl;
      navigate(target, { replace: true });
    }
  }, [hiddenHubLens, hubLens, flagsReady, navigate, routerLocation.search, routerLocation.hash, legacyWeek]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (hubLens === HUB_LENS.FINANCIALS && sensitivity.ready && !canSeeHubFinancials) {
      navigate(buildProjectsTabUrl(HUB_LENS.PORTFOLIO, routerLocation.search), { replace: true });
    }
  }, [hubLens, canSeeHubFinancials, sensitivity.ready, navigate, routerLocation.search]);

  // PMO Slice C — one shared "My weekly update" surface for both entry points.
  // weeklyRefresh bumps after contributor saves so the hub band and the live
  // current-week overview refetch (archives are never touched).
  const [myUpdate, setMyUpdate] = useState({ open: false, focusProjectId: null });
  const [weeklyRefresh, setWeeklyRefresh] = useState(0);
  const [updatesOverview, setUpdatesOverview] = useState(null);
  const [updatesToolbar, setUpdatesToolbar] = useState({ sortActive: false, count: 0, total: 0 });
  const [financialCount, setFinancialCount] = useState(0);
  const [participantControls, setParticipantControls] = useState(null);
  const [participantCount, setParticipantCount] = useState(null);
  const updatesResetRef = useRef(null);
  const onUpdatesToolbarChange = useCallback(({ resetSort, ...state }) => {
    updatesResetRef.current = resetSort;
    setUpdatesToolbar(state);
  }, []);
  const secondaryToolbar = [HUB_LENS.WEEKLY, HUB_LENS.GANTT, HUB_LENS.FINANCIALS, HUB_LENS.PEOPLE].includes(activeLens);
  const weeklyArchiveCache = useRef(new Map());
  const openWeeklyArchive = useCallback((week, projectId) => {
    changeWeeklyState({ week, weeklyProject: projectId, weeklyWeek: null, view: "agenda" });
  }, [changeWeeklyState]);
  const [myUpdatePending, setMyUpdatePending] = useState(false);
  const [closeAsk, setCloseAsk] = useState(false);
  const openMyUpdate = useCallback((focusProjectId) => setMyUpdate({ open: true, focusProjectId: focusProjectId || null }), []);
  const bumpWeekly = useCallback(() => setWeeklyRefresh((n) => n + 1), []);
  const closeRecordDrawerRef = useRef(null);
  const archiveReaderProject = useCallback((id) => {
    setProjects(rows => rows.filter(row => row.id !== id));
    closeRecordDrawerRef.current?.();
    setExpandedProjectId(current => current === id ? null : current);
    setPeekProjectId(current => current === id ? null : current);
    weeklyArchiveCache.current.clear();
    setWeeklyRefresh(n => n + 1);
  }, []);
  const updateReaderProject = useCallback((updated) => {
    if (!updated?.id) return;
    setProjects(rows => rows.map(row => row.id === updated.id ? { ...row, ...updated } : row));
    onProjectUpdated?.(updated);
  }, [onProjectUpdated]);

  useEffect(() => {
    if (typeof getProjectSourceInventory !== "function") return undefined;
    let cancelled = false;
    Promise.resolve().then(() => getProjectSourceInventory()).then((inventory) => {
      if (!cancelled) {
        setSourceInventory({
          portfolioSources: Array.isArray(inventory?.portfolioSources) ? inventory.portfolioSources : [],
          projectSources: Array.isArray(inventory?.projectSources) ? inventory.projectSources : [],
        });
      }
    }).catch(() => {
      // Source links are supplemental; the project table remains usable.
    });
    return () => { cancelled = true; };
  }, [refreshKey, financialRefresh]);

  useEffect(() => {
    setExpandedProjectId(initialExpandedProjectId || null);
    if (initialExpandedProjectId) setPeekProjectId(null);
  }, [initialExpandedProjectId]);

  // ── Fetch projects ─────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchErr(null);
    kpiReadyCountRef.current = 0;
    Promise.all([
      Promise.resolve().then(() => readAlertProjects(authIdentity)).then(list => {
        if (!cancelled) setAlertProjectState({ authIdentity, refreshKey, data: list });
        return list;
      }),
      Promise.resolve().then(() => getAllNextMilestones()).catch((err) => {
        log.warn("ProjectsPage: next milestones unavailable", { err: String(err) });
        return null;
      }),
    ])
      .then(([list, milestones]) => {
        if (!cancelled) {
           const milestonesError = !Array.isArray(milestones);
           const milestoneRows = milestonesError ? [] : milestones;
           const milestoneByProject = new Map(milestoneRows.map((milestone) => [milestone.projectId, milestone]));
           setProjects((Array.isArray(list) ? list : []).map((project) => ({
             ...project,
             nextMilestone: milestoneByProject.get(project.id) || null,
             milestonesError,
           })));
           setLoading(false);
           // The combined request settles both the project list and the
           // milestone-backed row data.
           kpiReadyCountRef.current += 2;
           if (kpiReadyCountRef.current >= 2) setProjAnimKey((k) => k + 1);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          log.error("ProjectsPage: fetch failed", { err: String(err) });
          setFetchErr(err.message || "Could not load projects.");
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [refreshKey, financialRefresh, authIdentity]);

  // ── In-place project list update from inline edit save ─────────────────────

  useEffect(() => {
    if (!projectPatch || !projectPatch.id) return;
    setProjects((prev) => prev.map((p) => p.id === projectPatch.id ? { ...p, ...projectPatch } : p));
  }, [projectPatch]);

  // ── Derived data ───────────────────────────────────────────────────────────

  // Dynamic owner dimension — recalculated when projects list changes
  const filterDimensionsWithScope = useMemo(() => [filterDimensions[0], SCOPE_DIMENSION, ...filterDimensions.slice(1)], [filterDimensions]);
  const filterSelectionsWithScope = useMemo(
    // Badge/panel reflect only effective restrictions: office scoping bypasses
    // saved facets/search/scope, and a Complete phase selection bypasses Scope.
    () => (scopedLocationId
      ? { ...normalizeCheckedSelections(filterDimensions), scope: [] }
      : {
        ...cfSelections,
        scope: projectScope === PROJECT_SCOPE.CAPITAL_MOVES && !hasCompletePhaseFilter(cfSelections)
          ? [PROJECT_SCOPE.CAPITAL_MOVES] : [],
      }),
    [cfSelections, projectScope, scopedLocationId],
  );
  const sortFields = useMemo(() => [
    { value: "fdobDate", label: "Target date" },
    { value: "name", label: "Project name" },
    { value: "projectType", label: "Type" },
    { value: "phase", label: "Phase" },
    { value: "statusOverall", label: "Status" },
    ...(canSeeBudget ? [{ value: "approvedBudget", label: "Budget (USD-converted)", numeric: true }] : []),
  ], [canSeeBudget]);

  // Office-scoped landing bypasses saved facets/search/scope. Do not claim
  // those saved preferences are narrowing the current population.
  const summaryChips = [];
  if (scopedLocationId) {
    const officeProject = projects.find((p) => p.locationId === scopedLocationId);
    const officeLabel = [officeProject?.locationName, officeProject?.locationAlias, scopedLocationId].filter(Boolean).join(" · ");
    summaryChips.push({
      id: "office", label: `Office: ${officeLabel}`,
      onRemove: typeof onClearLocationFilter === "function" ? onClearLocationFilter : null,
    });
  } else {
    if (projectScope === PROJECT_SCOPE.CAPITAL_MOVES && !hasCompletePhaseFilter(cfSelections)) {
      summaryChips.push({
        id: "scope", label: "Scope: Major projects",
        onRemove: () => setProjectScope(PROJECT_SCOPE.ALL),
      });
    }
    if (search.trim()) summaryChips.push({ id: "search", label: `Search: ${search}`, onRemove: () => setSearch("") });
    for (const group of activeFilterGroups(filterDimensions, cfSelections, { stage: DEFAULT_PROJECT_STAGES }, true)) {
      const labels = cfSelections[group.id].map((value) =>
        group.options.find((option) => option.value === value)?.label ?? value);
      summaryChips.push({
        id: group.id, label: `${group.label}: ${labels.join(", ")}`,
        onRemove: () => setCfSelections((current) => ({ ...current, [group.id]: group.id === "stage" ? [...DEFAULT_PROJECT_STAGES] : allOptionValues(group) })),
      });
    }
  }

  // Memoized so `filtered` is only a new reference when actual data/filter state changes.
  // Without useMemo this is a new array every render, which causes dependent
  // effects and parent navigation state to re-run on every render.
  // Enrich once (FX + sensitivity) before filter/sort; recomputed whenever the
  // raw project list changes, so patched records never keep a stale portfolio.
  const enrichedProjects = useMemo(() => {
    const enrichRates = fxRatesKey ? fxRatesRef.current : null;
    return projects.map((project) => enrichPortfolioProject(project, { rates: enrichRates, canSeeBudget }));
  }, [projects, fxRatesKey, canSeeBudget]);

  const filtered = useMemo(
    () => {
      // A Projects deep link from an office is an exact relationship filter.
      // Ignore persisted search/scope/filter preferences so they cannot hide
      // the linked office on landing.
      const scoped = applyProjectCfFilters(
        applyProjectScope(
          enrichedProjects,
          scopedLocationId || hasCompletePhaseFilter(cfSelections)
            ? PROJECT_SCOPE.ALL
            : projectScope,
        ),
        scopedLocationId ? { stage: "all" } : cfSelections,
        scopedLocationId ? "" : search,
        scopedLocationId ? null : filterDimensions,
      );
      const officeRows = scopedLocationId
        ? scoped.filter((project) => project.locationId === scopedLocationId)
        : scoped;
      const ratesForSort = fxRatesKey ? fxRatesRef.current : null;
      return applyProjectSort(officeRows, projectSortKey, projectSortDir, ratesForSort);
    },
    [enrichedProjects, projectScope, cfSelections, search, projectSortKey, projectSortDir, scopedLocationId, fxRatesKey, filterDimensions],
  );
  const collectionIds = useMemo(() => filtered.map(p => p.id), [filtered]);
  const currentFiltered = useMemo(() => filtered.filter(isCurrentProject), [filtered]);
  const hiddenSearchGroups = useMemo(() => scopedLocationId ? [] : restrictedFilterGroups(filterDimensions, cfSelections, true), [scopedLocationId, filterDimensions, cfSelections]);
  const hiddenSearchHint = useMemo(() => deriveFilterHiddenSearch({
    query: search, groups: hiddenSearchGroups, selections: cfSelections, checked: true,
    candidates: applyProjectCfFilters(applyProjectScope(enrichedProjects,
      hasCompletePhaseFilter(cfSelections) ? PROJECT_SCOPE.ALL : projectScope), { stage: "all" }, search),
    visible: filtered,
  }), [search, hiddenSearchGroups, cfSelections, enrichedProjects, projectScope, filtered]);
  const revealHiddenSearch = () => setCfSelections(current => ({
    ...relaxFilterGroups(current, hiddenSearchGroups, true),
    stage: Object.keys(PROJECT_STAGES),
  }));
  const countedProjectDimensions = useMemo(() => withFacetCounts(
    filterDimensionsWithScope, filterSelectionsWithScope, enrichedProjects,
    (rows, selected, countGroup) => {
      if (scopedLocationId) return rows.filter(project => project.locationId === scopedLocationId);
      const scope = hasCompletePhaseFilter(selected) ? PROJECT_SCOPE.ALL
        : selected.scope?.includes(PROJECT_SCOPE.CAPITAL_MOVES) ? PROJECT_SCOPE.CAPITAL_MOVES : PROJECT_SCOPE.ALL;
      return applyProjectCfFilters(applyProjectScope(rows, scope), selected, search, filterDimensions, countGroup);
    },
  ), [filterDimensionsWithScope, filterSelectionsWithScope, enrichedProjects, scopedLocationId, search, filterDimensions]);
  // The milestone read enriches/sorts rows, but does not contribute any alert.
  const alertSource = alertProjects === null ? null : loading ? alertProjects : projects;
  const alertFiltered = useMemo(() => {
    if (!alertSource) return [];
    const rows = applyProjectCfFilters(
      applyProjectScope(alertSource, scopedLocationId || hasCompletePhaseFilter(cfSelections) ? PROJECT_SCOPE.ALL : projectScope),
      scopedLocationId ? { stage: "all" } : cfSelections,
      scopedLocationId ? "" : search,
      scopedLocationId ? null : filterDimensions,
    );
    return scopedLocationId ? rows.filter(p => p.locationId === scopedLocationId) : rows;
  }, [alertSource, projectScope, cfSelections, search, scopedLocationId, filterDimensions]);
  const alertIds = useMemo(() => alertFiltered.filter(isActiveHubProject).map(p => p.id), [alertFiltered]);
  // Shared server-owned current-week overview (one read for every row, the
  // peek and nothing else); contributor/decision counts via getHubWeekly for
  // the same active filtered IDs pinned to that server week.
  const [weekState, retryWeek] = useCurrentProjectWeek("projects-landing", weeklyRefresh, activeLens !== HUB_LENS.WEEKLY);
  const activeCollectionIds = useMemo(() => (alertSource || []).filter(isActiveHubProject).map(p => p.id), [alertSource]);
  const landingWeekly = useLandingWeekly(activeLens === HUB_LENS.WEEKLY ? activeCollectionIds : alertIds, weekState, `${weeklyRefresh}|${refreshKey}|${financialRefresh}`, `${authIdentity}|${currentRole}`);
  const retryLandingWeekly = () => { if (weekState.error) retryWeek(); landingWeekly.retry(); };

  const lastAlertMessagesRef = useRef([]);
  const builtAlertMessages = buildProjectAlertMessages({
    projects: alertFiltered.filter(isCurrentProject),
    projectsReady: alertProjects !== null && !fetchErr,
    hub: landingWeekly.empty ? { loading: false, error: null, data: null } : landingWeekly,
    onShowAtRisk: () => {
      setCfSelections((current) => ({ ...current, health: ["at_risk", "critical"] }));
      setHubLens(HUB_LENS.PORTFOLIO);
    },
    onOpenWeekly: () => setHubLens(HUB_LENS.WEEKLY),
    onRetryWeekly: retryLandingWeekly,
  });
  // Pending/failed reads hold the last known messages rather than a false all-clear.
  if (builtAlertMessages) lastAlertMessagesRef.current = builtAlertMessages;
  const projectAlertMessages = builtAlertMessages || lastAlertMessagesRef.current;

  const projectSourcesByProject = useMemo(() => {
    const map = new Map();
    for (const source of sourceInventory.projectSources) {
      if (!map.has(source.projectId)) map.set(source.projectId, []);
      map.get(source.projectId).push(source);
    }
    return map;
  }, [sourceInventory.projectSources]);

  const updateProjectSources = (updater) => {
    setSourceInventory((current) => ({
      ...current,
      projectSources: typeof updater === "function" ? updater(current.projectSources) : updater,
    }));
  };

  // KPI-SCOPE-INDICATOR (G-22/A1): these list KPIs describe the visible
  // project set, not the unfiltered portfolio. Keep their values aligned with
  // the responsive cue shown by KpiTile.
  const stats = useMemo(
    () => computeProjectStats(filtered, { rates }),
    [filtered, rates],
  );
  // ── Increment animation key when filters change post-load ─────────────────
  // Gate on kpiReadyCountRef >= 2: while projects or milestones are still
  // loading, filtered changes (e.g. projects arriving) don't trigger a
  // premature second animation — the initial animKey fires from the
  // fetch effects above. After both are ready, each subsequent filter/search
  // change re-runs the cascade, matching Offices/Leases behavior.
  // NOTE: this effect must stay AFTER the `filtered` useMemo declaration above.
  useEffect(() => {
    if (kpiReadyCountRef.current < 2) return;
    setProjAnimKey((k) => k + 1);
  }, [filtered]);

  const hasActiveFilter = summaryChips.length > 0;

  // Row click / caret / Enter: one local briefing peek at a time.
  function focusRowSoon(projectId) {
    setTimeout(() => {
      const row = document.querySelector(`[data-testid="project-row-${projectId}"]`);
      if (row && typeof row.focus === "function") row.focus();
    }, 0);
  }

  function handleTogglePeek(projectId) {
    // Closing (caret, Collapse, row) returns focus to the project row.
    if (projectId === peekProjectId) focusRowSoon(projectId);
    setPeekProjectId((current) => (current === projectId ? null : projectId));
    if (projectId !== peekProjectId && typeof onRowClick === "function") onRowClick(projectId);
  }

  // Open full project: existing routed deep link (office scope is kept by the
  // AppShell URL callback). Falls back to local full detail without a router.
  function handleOpenFull(projectId) {
    setPeekProjectId(null);
    setExpandedProjectId(projectId);
    if (typeof onExpandedProjectChange === "function") onExpandedProjectChange(projectId);
  }

  // Round 4: Portfolio row activation opens the shared record drawer (no
  // inline content, no navigation). Name links / Open project go to the
  // canonical project-scoped Updates URL.
  // Task #9085: the drawer is route-owned. /projects/{code|id}?tab= selects
  // the record and tab; identity resolves through the loaded client rows
  // (assigned code first, then ID). An unresolved ref is handed to the
  // record reader as an ID so it shows its own missing/denied state.
  const recordDrawerGuard = useRef(null);
  const resolvedDrawerProject = useMemo(() => (drawerRef ? resolveProjectRef(drawerRef, projects) : null), [drawerRef, projects]);
  const drawerProjectId = drawerRef && !loading ? (resolvedDrawerProject?.id ?? drawerRef) : null;
  const recordDrawer = useMemo(() => (drawerProjectId ? { projectId: drawerProjectId } : null), [drawerProjectId]);
  const drawerTabParam = query.get("tab");
  const drawerTab = PROJECT_DRAWER_TABS.includes(drawerTabParam) ? drawerTabParam : "overview";
  const drawerReturn = readRecordReturn(routerLocation.state);
  const listSearch = useMemo(() => {
    const params = new URLSearchParams(routerLocation.search);
    params.delete("tab");
    const q = params.toString();
    return q ? `?${q}` : "";
  }, [routerLocation.search]);
  const drawerPathFor = useCallback((project, tab = "overview") => {
    const ref = project ? projectCanonicalRef(project) : null;
    if (!ref) return null;
    const params = new URLSearchParams(listSearch);
    if (tab && tab !== "overview") params.set("tab", tab);
    const q = params.toString();
    return `/projects/${encodeURIComponent(ref)}${q ? `?${q}` : ""}`;
  }, [listSearch]);
  // Canonicalize an ID link to the assigned code (replace, same record).
  useEffect(() => {
    if (!drawerRef || !resolvedDrawerProject) return;
    const canonical = projectCanonicalRef(resolvedDrawerProject);
    if (canonical && canonical !== drawerRef) {
      navigate(`${drawerPathFor(resolvedDrawerProject, drawerTab)}${routerLocation.hash}`, { replace: true, state: routerLocation.state });
    }
  }, [drawerRef, resolvedDrawerProject]); // eslint-disable-line react-hooks/exhaustive-deps
  const setRecordDrawer = useCallback((next) => {
    if (next) return;
    if (drawerRef) navigate(`${routerLocation.state?.financialsReturn ? "/projects/financials" : routerLocation.state?.travelReturn ? "/projects/travel" : routerLocation.state?.participantsReturn ? "/projects/team" : "/projects"}${listSearch}${routerLocation.hash}`, { state: routerLocation.state });
  }, [drawerRef, listSearch, navigate, routerLocation.hash, routerLocation.state]);
  closeRecordDrawerRef.current = () => setRecordDrawer(null);
  const openRecordDrawer = useCallback((projectId, opener, recordTab = "overview") => {
    // The drawer's registered router guard explains/resolves unsaved work;
    // never silently swallow a collection-row navigation here.
    opener?.focus({ preventScroll: true });
    const row = projects.find((p) => p.id === projectId) ?? { id: projectId };
    // Return ownership must describe this opener, not an older drawer visit.
    const state = { ...routerLocation.state };
    delete state.financialsReturn; delete state.participantsReturn; delete state.travelReturn;
    if (activeLens === HUB_LENS.FINANCIALS) state.financialsReturn = true;
    if (activeLens === HUB_LENS.PEOPLE) state.participantsReturn = true;
    if (activeLens === HUB_LENS.TRAVEL) state.travelReturn = true;
    navigate(`${drawerPathFor(row, recordTab)}${routerLocation.hash}`, { state });
  }, [projects, drawerPathFor, navigate, routerLocation.hash, routerLocation.state, activeLens]);
  const pageDrawerProject = useCallback((projectId) => {
    const row = projects.find((p) => p.id === projectId) ?? { id: projectId };
    // A tab belongs to one record; opening another record starts at Overview.
    navigate(`${drawerPathFor(row)}${routerLocation.hash}`, { replace: true, state: routerLocation.state });
  }, [projects, drawerPathFor, navigate, routerLocation.state, routerLocation.hash]);
  const changeDrawerTab = useCallback((tab) => {
    const row = resolvedDrawerProject ?? (drawerProjectId ? { id: drawerProjectId } : null);
    const next = typeof tab === "function" ? tab(drawerTab) : tab;
    if (row && next !== drawerTab) navigate(`${drawerPathFor(row, next)}${routerLocation.hash}`, { state: routerLocation.state });
  }, [resolvedDrawerProject, drawerProjectId, drawerPathFor, drawerTab, navigate, routerLocation.state, routerLocation.hash]);
  // Explicit "Open project" / name links go to the Updates reader.
  const openCanonicalProject = useCallback((projectId) => {
    navigate(buildProjectUpdatesUrl(projectId, routerLocation.search));
  }, [navigate, routerLocation.search]);

  function handleCollapseFull() {
    setExpandedProjectId(null);
    if (typeof onExpandedProjectChange === "function") onExpandedProjectChange(null);
  }

  // ── Keyboard navigation (mirrors OfficesPage pattern) ─────────────────────

  const selectPeek = (id) => {
    setPeekProjectId(id);
    if (id && typeof onRowClick === "function") onRowClick(id);
  };
  const toggleProjectRef = useRef(selectPeek);
  toggleProjectRef.current = selectPeek;

  const selectedProjectIdx = useMemo(
    () => selectedProjectId ? filtered.findIndex((p) => p.id === selectedProjectId) : -1,
    [selectedProjectId, filtered],
  );

  useEffect(() => {
    if (recordDrawer || selectedProjectId == null || activeLens !== HUB_LENS.PORTFOLIO) return;
    function onKeyDown(e) {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable) return;
      if (e.key === "Escape") {
        const closing = selectedProjectId;
        toggleProjectRef.current(null);
        setTimeout(() => document.querySelector(`[data-testid="project-row-${closing}"]`)?.focus?.(), 0);
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        const nextIdx = Math.min(selectedProjectIdx + 1, filtered.length - 1);
        if (nextIdx !== selectedProjectIdx && filtered[nextIdx]) {
          const next = filtered[nextIdx];
          toggleProjectRef.current(next.id);
        }
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        const prevIdx = Math.max(selectedProjectIdx - 1, 0);
        if (prevIdx !== selectedProjectIdx && filtered[prevIdx]) {
          const prev = filtered[prevIdx];
          toggleProjectRef.current(prev.id);
        }
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedProjectId, selectedProjectIdx, filtered, activeLens, recordDrawer]);

  useEffect(() => {
    if (selectedProjectId == null) return;
    const row = document.querySelector(`[data-testid="project-row-${selectedProjectId}"]`);
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedProjectId]);

  // Resolve a URL deep link only after the list is available. An ID that is
  // not in the current list safely returns to the canonical list URL.
  useEffect(() => {
    if (loading || !initialExpandedProjectId) return;
    const targetProject = projects.find((p) => p.id === initialExpandedProjectId);
    if (!targetProject) {
      if (typeof onExpandedProjectChange === "function") onExpandedProjectChange(null);
      return;
    }
    if (projectScope !== PROJECT_SCOPE.ALL && !isCapitalOrMoveProject(targetProject)) {
      setProjPrefs((state) => ({ ...state, scope: PROJECT_SCOPE.ALL }));
      return;
    }
    if (expandedProjectId !== initialExpandedProjectId) {
      setExpandedProjectId(initialExpandedProjectId);
    }
    const row = document.querySelector(`[data-testid="project-row-${initialExpandedProjectId}"]`);
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [loading, projects, projectScope, initialExpandedProjectId, expandedProjectId, onExpandedProjectChange, setProjPrefs]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleClear() {
    if (scopedLocationId && typeof onClearLocationFilter === "function") {
      onClearLocationFilter();
    }
    setCfSelections({});
    if (!secondaryToolbar) setSearch("");
    setProjectSortKey(null);
    setProjectSortDir(1);
    setProjectScope(PROJECT_SCOPE.ALL);
    if (activeLens === HUB_LENS.WEEKLY) updatesResetRef.current?.();
  }

  function handleProjectSort(key, dir) {
    if (!VALID_PROJECT_SORT_KEYS.includes(key)) return;
    setProjectSortKey(key);
    setProjectSortDir(dir);
  }

  const handleProjectCreated = useCallback((newProject) => {
    log.ui("ProjectsPage: project created", { id: newProject.id });
    setProjects((prev) => [newProject, ...prev]);
  }, []);

  const handleProjectUpdated = useCallback((updated) => {
    setProjects((prev) => prev.map((project) => project.id === updated.id ? { ...project, ...updated } : project));
    Promise.resolve()
      .then(() => getAllNextMilestones())
      .then((milestones) => {
        const milestoneByProject = new Map(
          (Array.isArray(milestones) ? milestones : []).map((milestone) => [milestone.projectId, milestone]),
        );
        setProjects((prev) => prev.map((project) => ({
          ...project,
          nextMilestone: milestoneByProject.get(project.id) || null,
          milestonesError: false,
        })));
      })
      .catch((err) => {
        log.warn("ProjectsPage: milestone refresh after project edit failed", { err: String(err) });
        setProjects((prev) => prev.map((project) => ({ ...project, milestonesError: true })));
      });
    if (typeof onProjectUpdated === "function") onProjectUpdated(updated);
  }, [onProjectUpdated]);

  // ── KPI tile values ────────────────────────────────────────────────────────

  const totalBudgetStr = stats.totalBudget > 0
    ? `$${(stats.totalBudget / 1_000_000).toFixed(0)}M${stats.budgetUnavailableCount > 0 ? "*" : ""}`
    : "—";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      data-testid="projects-page"
      className={`projects-landing${activeLens === HUB_LENS.WEEKLY ? " projects-updates-landing" : activeLens === HUB_LENS.PORTFOLIO ? " projects-portfolio-landing" : activeLens === HUB_LENS.PEOPLE ? " projects-team-landing" : ""}`}
      style={{ paddingBottom: 0, minHeight: 0, "--projects-kpi-height": `${KPI_TILE_ROW_HEIGHT}px` }}
    >
      <ProjectsNavigation>
        <ProjectsHubBar lens={activeLens} onLensChange={setHubLens} showFinancials={canSeeHubFinancials} navigationOnly />
      </ProjectsNavigation>
      {fetchErr && (
        <ErrorBanner error={fetchErr ? new Error(fetchErr) : null} onDismiss={() => setFetchErr(null)} />
      )}

      <ProjectsScrollLayout>

        {/* ── KPI tiles + toolbar + count ─────────────────────────────────── */}
        {activeLens !== HUB_LENS.TRAVEL && <>
            {activeLens === HUB_LENS.WEEKLY && <UpdatesKpiBand projects={projects} loading={loading}
              error={fetchErr} onRetry={() => setFinancialRefresh(n => n + 1)}
              posted={landingWeekly} onRetryPosted={retryLandingWeekly}
              overview={updatesOverview?.data && overviewWeek && updatesOverview.data.weekDate !== overviewWeek ? { loading: true } : updatesOverview}
              refreshToken={weeklyRefresh} />}
            {/* KPI tiles — lean project view */}
            {activeLens !== HUB_LENS.WEEKLY && <div
              data-testid="projects-kpi-strip"
              style={{
                position: "static",
                background: "var(--v2-bg)",
                padding: 0,
              }}
            >
            {/* Projects KPI copy reviewed ahead of navigation enablement;
                keep the existing visibility gates unchanged. */}
            <KpiBand testId="projects-kpi-row">
              <KpiTile
                testId="projects-stat-active"
                height={KPI_TILE_ROW_HEIGHT}
                label="Active projects"
                tooltip={<KpiPopover period="Today" source="Ripple projects">
                  Projects at Stage Active in the current view.
                </KpiPopover>}
                tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
                tooltipPlacement="viewport-clamped"
                descriptor="in flight now"
                value={loading ? "…" : stats.activeCount}
                rawValue={loading ? undefined : stats.activeCount}
                formatter={(n) => fmtInt(n)}
                animationKey={projAnimKey}
                tileIndex={0}
                scope="responsive"
                isFiltered={hasActiveFilter}
                color={T.dark}
              />
              <KpiTile
                testId="projects-stat-at-risk"
                height={KPI_TILE_ROW_HEIGHT}
                label="At risk"
                tooltip={<KpiPopover period="Today" source="Ripple projects">
                  Active projects marked off-track, critical, or flagged in the current view.
                </KpiPopover>}
                tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
                tooltipPlacement="viewport-clamped"
                descriptor="off-track or flagged"
                value={loading ? "…" : stats.atRiskCount}
                rawValue={loading ? undefined : stats.atRiskCount}
                formatter={(n) => fmtInt(n)}
                animationKey={projAnimKey}
                tileIndex={1}
                scope="responsive"
                isFiltered={hasActiveFilter}
                color={!loading && !fetchErr && stats.atRiskCount > 0 ? T.amber : T.dark}
              />
              <KpiTile
                testId="projects-stat-exits"
                height={KPI_TILE_ROW_HEIGHT}
                label="Exits in progress"
                tooltip={<KpiPopover period="Today" source="Ripple projects">
                  Exit projects at Stage Active in the current view.
                </KpiPopover>}
                tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
                tooltipPlacement="viewport-clamped"
                descriptor="decommissions underway"
                value={loading ? "…" : stats.exitsInProgressCount}
                rawValue={loading ? undefined : stats.exitsInProgressCount}
                formatter={(n) => fmtInt(n)}
                animationKey={projAnimKey}
                tileIndex={2}
                scope="responsive"
                isFiltered={hasActiveFilter}
                color={T.dark}
              />
              <KpiTile
                testId="projects-stat-net-seats"
                height={KPI_TILE_ROW_HEIGHT}
                label="Net seats coming"
                tooltip={<KpiPopover period="Today" source="Projects + office capacity">
                  For each office in the current view, planned seats minus current installed capacity; each office is counted once.
                </KpiPopover>}
                tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
                tooltipPlacement="viewport-clamped"
                descriptor="planned less current"
                value={loading ? "…" : stats.netSeatsComing}
                rawValue={loading ? undefined : stats.netSeatsComing}
                formatter={(n) => fmtInt(n)}
                animationKey={projAnimKey}
                tileIndex={3}
                scope="responsive"
                isFiltered={hasActiveFilter}
                color={T.dark}
              />
              <KpiTile
                testId="projects-stat-delivering"
                height={KPI_TILE_ROW_HEIGHT}
                label="Delivering ≤12 mo"
                tooltip={<KpiPopover period="Today" source="Ripple projects">
                  Active projects with a first day of business from today through the next 12 months.
                </KpiPopover>}
                tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
                tooltipPlacement="viewport-clamped"
                descriptor="FDOB within a year"
                value={loading ? "…" : stats.deliveringWithin12MonthsCount}
                rawValue={loading ? undefined : stats.deliveringWithin12MonthsCount}
                formatter={(n) => fmtInt(n)}
                animationKey={projAnimKey}
                tileIndex={4}
                scope="responsive"
                isFiltered={hasActiveFilter}
                color={T.dark}
              />
              {canSeeBudget && <KpiTile
                testId="projects-stat-budget"
                height={KPI_TILE_ROW_HEIGHT}
                label="Total Budget"
                tooltip={<KpiPopover period="Today" source="Projects + FX rates (derived)">
                  Approved budgets for active projects in the current view, converted to USD where exchange rates are available; projects without a usable rate are excluded.
                  {" "}{stats.untrackedCount} not tracked (n/a), excluded from Total Budget.
                </KpiPopover>}
                tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
                tooltipPlacement="viewport-clamped"
                descriptor="approved capital"
                value={loading ? "…" : totalBudgetStr}
                rawValue={loading || stats.totalBudget <= 0 || stats.budgetUnavailableCount > 0 ? undefined : stats.totalBudget / 1_000_000}
                formatter={(n) => "$" + Math.round(n) + "M"}
                animationKey={projAnimKey}
                tileIndex={5}
                scope="responsive"
                isFiltered={hasActiveFilter}
                color={T.dark}
              />}
            </KpiBand>
            </div>}
            {activeLens !== HUB_LENS.WEEKLY && canSeeBudget && <ProjectsFxStatus
              loading={fxLoading} error={fxError} partial={stats.budgetUnavailableCount > 0}
              floating={activeLens === HUB_LENS.PORTFOLIO}
            />}

            {/* Toolbar row */}
            <InlineFilterToolbar
              collectionActions
              testId="projects-toolbar"
              countBelow={activeLens === HUB_LENS.PORTFOLIO || secondaryToolbar}
              reuseCountGap={activeLens === HUB_LENS.PORTFOLIO}
              leftTestId="projects-toolbar-left"
              busy={Boolean(weekState.refreshing || landingWeekly.refreshing)}
              countTestId="projects-showing-count"
              countContent={activeLens === HUB_LENS.PEOPLE ? <span ref={setParticipantCount} data-testid="projects-showing-count" style={{ display: "block", textAlign: "left", marginTop: 4, lineHeight: "16px", fontSize: T.fsSm, color: T.collectionCount }} /> : undefined}
              count={activeLens === HUB_LENS.WEEKLY ? updatesToolbar.count : activeLens === HUB_LENS.FINANCIALS ? financialCount : filtered.length}
              total={activeLens === HUB_LENS.WEEKLY ? updatesToolbar.total : projects.length}
              trailing={
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <ViewControls
                object="projects"
                view={viewMode}
                viewToggle={activeLens === HUB_LENS.GANTT ? (
                  <span data-testid="projects-timeline-key-control" style={{ display: "inline-flex" }}>
                    <ProjectsTimelineKey />
                  </span>
                ) : activeLens === HUB_LENS.PEOPLE ? <span ref={setParticipantControls} style={{ display: "inline-flex", alignItems: "center", gap: 8 }} /> : null}
                densityNodes={![HUB_LENS.PORTFOLIO, HUB_LENS.GANTT].includes(activeLens) ? {} : {
                  [DENSITY_KIND.COMPACT_COMFORTABLE]: () => (
                    <CompactSwitch
                      checked={density === DENSITY_COMPACT}
                      onChange={() => {
                        const next = density === DENSITY_COMFORTABLE ? DENSITY_COMPACT : DENSITY_COMFORTABLE;
                        setDensity(next);
                      }}
                      testId="projects-density-toggle"
                    />
                  ),
                }}
                addButton={canWrite ? (
                  <ToolbarAddButton variant="primary" testId="add-project-btn" aria-label="+ Add project" onClick={() => setModalOpen(true)}>
                    Add project
                  </ToolbarAddButton>
                ) : null}
              />
              </div>}
            >
                <AlertChip
                  testId="projects-alert-chip"
                  storageKey="ripple.projects.alertChip.v1.open"
                  messages={projectAlertMessages}
                  reservedWidth={T.alertChipSlotW}
                  pending={builtAlertMessages === null}
                  okMessage={{ text: "No project alerts" }}
                />
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Search projects…"
                  testId="projects-search"
                  style={activeLens === HUB_LENS.WEEKLY ? {
                    borderRadius: 9, height: 33, boxSizing: "border-box",
                  } : undefined}
                />

                <ConsolidatedFilter
                  unifiedDefaults
                  checkedInclusion
                  baselineSelections={{ stage: DEFAULT_PROJECT_STAGES, scope: [] }}
                  dimensions={countedProjectDimensions}
                  selections={filterSelectionsWithScope}
                  onChange={(sel) => {
                    const { scope, ...rest } = sel || {};
                    setProjectScope(scope?.includes(PROJECT_SCOPE.CAPITAL_MOVES) ? PROJECT_SCOPE.CAPITAL_MOVES : PROJECT_SCOPE.ALL);
                    setCfSelections(rest);
                    setProjectSortKey(null);
                    setProjectSortDir(1);
                  }}
                  testId="projects-filter-btn"
                />

                <InlineFilterChips prefix="projects" chips={summaryChips.map((chip) => ({
                  ...chip, removeLabel: `Remove ${chip.id === "scope" ? "Major projects scope (select All)" : `${chip.id} filter`}`,
                }))} />
                {activeLens === HUB_LENS.WEEKLY ? <span id="updates-toolbar-sort" style={{ display: "inline-flex", marginRight: 10 }} /> : <>
                <SortControl
                  testId="projects-sort"
                  fields={sortFields}
                  primary={{ key: projectSortKey, dir: projectSortDir > 0 ? "asc" : "desc" }}
                  secondary={{ key: null, dir: "asc" }}
                  showSecondary={false}
                  defaultKey="fdobDate"
                  onChange={({ primary }) => {
                    if (primary?.key) handleProjectSort(primary.key, primary.dir === "desc" ? -1 : 1);
                  }}
                />
                {activeLens === HUB_LENS.GANTT && <ProjectsTimelineRange value={timelineRange} onChange={setTimelineRange} />}
                </>}

                {(secondaryToolbar
                  ? summaryChips.some(chip => chip.id !== "search") || (activeLens === HUB_LENS.WEEKLY
                    ? updatesToolbar.sortActive
                    : projectSortKey !== "fdobDate" || projectSortDir !== 1)
                  : hasActiveFilter || (activeLens === HUB_LENS.PORTFOLIO && (projectSortKey !== "fdobDate" || projectSortDir !== 1))) && (
                  <InlineFilterClear testId="projects-filters-clear" onClick={handleClear} />
                )}
            </InlineFilterToolbar>
            <FilterHiddenSearchHint hint={hiddenSearchHint} entity="project" onClear={revealHiddenSearch} empty={filtered.length === 0} />
        </>}

        <div className="projects-lens-content">
        {activeLens === HUB_LENS.TRAVEL && <ProjectsTravel key={authIdentity} authIdentity={authIdentity} onOpenProject={openRecordDrawer} />}
        {activeLens === HUB_LENS.WEEKLY && <WeeklyOverview onArchived={archiveReaderProject} authIdentity={authIdentity} populationReady={alertSource !== null || !hasActiveFilter} onToolbarChange={onUpdatesToolbarChange} connectionsByProjectId={connectionsByProjectId} currentRole={currentRole} onProjectUpdated={updateReaderProject} onMetricsChange={setUpdatesOverview} onAcknowledgedSave={bumpWeekly} canWrite={canWrite} selectedWeekDate={overviewWeek} selectedView={overviewView} onUrlStateChange={changeWeeklyState} initialProjectId={overviewProject} projectIds={alertSource === null && !hasActiveFilter ? null : alertIds} onOpenMyUpdate={openMyUpdate} refreshToken={weeklyRefresh} />}
        {activeLens === HUB_LENS.SIGNALS && <ProjectsSignals />}
        {activeLens === HUB_LENS.CONTRACTS && <ProjectsContracts />}
        {activeLens === HUB_LENS.RISK_REGISTER && showRiskRegister && !loading && <>
          {riskProjectId && <div data-testid="risk-project-scope" style={{ color: T.mid, fontSize: T.fsSm, marginBottom: 12 }}>
            Project: {riskProjects[0]?.name || riskProjectId}
            <button type="button" onClick={() => navigate(buildProjectsTabUrl(HUB_LENS.RISK_REGISTER, routerLocation.search, { riskProject: null }))} style={{ marginLeft: 10 }}>Clear project scope</button>
            {fetchErr || riskProjects.length === 0 ? <div role="status">Project unavailable - it may be unknown, complete, deleted, or could not be loaded.</div> : null}
          </div>}
          <ProjectRiskRegister projects={riskProjects || filtered.filter(isActiveHubProject)} currentRole={currentRole} />
        </>}
        {activeLens === HUB_LENS.PEOPLE && (loading
          ? <div style={{ padding: 48, textAlign: "center", color: T.mid, fontSize: T.fsMd }}>Loading projects…</div>
          : <PeopleRolesMatrix projects={filtered} currentRole={currentRole} authIdentity={authIdentity} density={density} onOpenRecord={openRecordDrawer} controlsTarget={participantControls} countTarget={participantCount} total={projects.length} />)}
        {activeLens === HUB_LENS.GANTT && (loading
          ? <div style={{ padding: 48, textAlign: "center", color: T.mid, fontSize: T.fsMd }}>Loading projects…</div>
          : <ProjectsGantt projects={currentFiltered} range={timelineRange} density={density} />)}
        {activeLens === HUB_LENS.FINANCIALS && canSeeHubFinancials && !loading && (
          <>
            {SHOW_LENS_FINANCIAL_IMPORT && currentRole === "admin" && canSeeHubFinancials && <button type="button" onClick={() => setFinancialImportOpen(true)}
              style={{ marginBottom: 12, padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 6, background: T.white, color: T.dark, cursor: "pointer" }}>
              Import project financials (CSV)
            </button>}
            <ProjectsFinancials onCountChange={setFinancialCount} projectIds={collectionIds} canExport={canExportFinancials} refreshToken={financialRefresh}
              onOpenProject={openRecordDrawer} projectHref={id => drawerPathFor(projects.find(p => p.id === id) ?? { id }, "financials")} />
          </>
        )}
        {SHOW_LENS_FINANCIAL_IMPORT && financialImportOpen && currentRole === "admin" && canSeeHubFinancials && isTabEnabled(HUB_LENS.FINANCIALS) && <ProjectFinancialsImportModal
          onClose={() => setFinancialImportOpen(false)} onImported={() => setFinancialRefresh(value => value + 1)} />}

        {/* ── Single inline project list (kept mounted under other lenses
             so expansion/selection survive lens switches) ───────────── */}
        <div
          data-testid="projects-portfolio-lens"
          hidden={activeLens !== HUB_LENS.PORTFOLIO}
          style={{ ...LIST_PRESENTATION.container, overflow: "visible", ...(activeLens !== HUB_LENS.PORTFOLIO ? { display: "none" } : null) }}
        >
          {loading ? (
            <div style={{ padding: 48, textAlign: "center", color: T.mid, fontSize: T.fsMd }}>
              Loading projects…
            </div>
          ) : (
            <ProjectsPortfolioList
              collection
              projects={filtered}
              onTogglePeek={handleTogglePeek}
              onOpenFull={openCanonicalProject}
              onOpenRecord={openRecordDrawer}
              sortKey={projectSortKey}
              sortDir={projectSortDir}
              onSort={handleProjectSort}
              density={density}
              peekProjectId={peekProjectId}
              expandedProjectId={expandedProjectId}
              currentRole={currentRole}
              onProjectUpdated={handleProjectUpdated}
              onArchived={archiveReaderProject}
              onCollapseFull={handleCollapseFull}
              projectSourcesByProject={projectSourcesByProject}
              portfolioSources={sourceInventory.portfolioSources}
              onSourcesChange={updateProjectSources}
              onOpenMyUpdate={openMyUpdate}
              weeklyRefresh={weeklyRefresh}
              weeklyArchiveCache={weeklyArchiveCache.current}
              onOpenWeeklyArchive={openWeeklyArchive}
              weekState={weekState}
              onRetryWeek={retryWeek}
              canSeeBudget={canSeeBudget}
            />
          )}
        </div>
        </div>

        <Modal isOpen={myUpdate.open} onClose={() => { if (myUpdatePending) { setCloseAsk(true); return; } setCloseAsk(false); setMyUpdate({ open: false, focusProjectId: null }); bumpWeekly(); }} maxWidth={720} title="My weekly update" ariaLabelledBy="my-weekly-update-title" testId="my-weekly-update-dialog">
          {closeAsk && (
            <div role="alert" data-testid="my-update-close-ask" style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 18px", background: T.utilAmber, fontSize: T.fsSm }}>
              <span style={{ flex: 1 }}>Changes are still saving. Closing now can cancel saves that have not started; unsent drafts are kept for this session.</span>
              <button type="button" data-testid="my-update-close-keep" onClick={() => setCloseAsk(false)} style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}>Keep open</button>
              <button type="button" data-testid="my-update-close-anyway" onClick={() => { setCloseAsk(false); setMyUpdatePending(false); setMyUpdate({ open: false, focusProjectId: null }); bumpWeekly(); }} style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 6, padding: "3px 10px", cursor: "pointer" }}>Close anyway</button>
            </div>
          )}
          {myUpdate.open && <div style={{ maxHeight: "78vh", overflowY: "auto" }}><MyWeeklyUpdate focusProjectId={myUpdate.focusProjectId} onChanged={bumpWeekly} onPendingChange={setMyUpdatePending} currentRole={currentRole} /></div>}
        </Modal>

      </ProjectsScrollLayout>

      <ProjectRecordDrawer
        key={authIdentity}
        open={Boolean(recordDrawer)}
        projectId={recordDrawer?.projectId}
        projectName={projects.find((row) => row.id === recordDrawer?.projectId)?.name || ""}
        origin="portfolio"
        currentRole={currentRole}
        guardRef={recordDrawerGuard}
        onClose={() => setRecordDrawer(null)}
        orderedProjectIds={filtered.map(project => project.id)}
        onOpenProject={(id) => { if (filtered.some(project => project.id === id)) pageDrawerProject(id); }}
        recordTab={drawerTab}
        onRecordTabChange={changeDrawerTab}
        copyPath={drawerProjectId ? buildProjectUrl(resolvedDrawerProject ? projectCanonicalRef(resolvedDrawerProject) : drawerProjectId, drawerTab === "overview" ? "" : `?tab=${drawerTab}`) : null}
        recordReturn={drawerReturn}
        onRecordSaved={updateReaderProject}
        onArchived={archiveReaderProject}
        participation={portfolioParticipation(weekState, recordDrawer?.projectId, retryWeek)}
      />

      {/* Shared create modal */}
      <ProjectEditModal
        isOpen={modalOpen}
        mode="create"
        onClose={() => setModalOpen(false)}
        onCreated={handleProjectCreated}
      />

    </div>
  );
}
